import { useURLParameters } from 'hooks';
import {
  RemovePositionDetailComponentMap,
  RemovePositionDetailModalViewType,
} from 'pages/LocationSettingsPage/PositionsAllocation/RemovePositionModal/RemovePositionModal.constants';
import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { ModalRoutingService } from 'services';
import { JaneConnection, OrderConnection } from 'types/graphql';
import { castStringToInteger } from 'utils';

export const removePositionModalRoutingService = new ModalRoutingService(
  'removePosition',
  'RemovePositionView',
  RemovePositionDetailComponentMap,
  { shiftId: 'number' },
  [
    'RemovePositionView',
    'PositionTeamMembersView',
    'RemoveOpenShiftView',
    'RemoveFlow',
    'JaneDetailsView',
    'OpenShiftsView',
  ],
);

export type RemovePositionFlowType = JaneConnection['__typename'] | OrderConnection['__typename'];

export interface IRemovePositionModalUI {
  isOpen: boolean;
  onClose: () => void;
  goToNextView: (removePositionFlowType?: {
    removePositionFlowType?: RemovePositionFlowType;
  }) => void;
  removePositionActiveModalView: RemovePositionDetailModalViewType;
  positionId: string;
  janeId?: string;
  shift_id?: number;
  isConfirmDeleteOpen: boolean;
  setIsConfirmDeleteOpen: Dispatch<SetStateAction<boolean>>;
  locationId: string;
}

export const RemovePositionUIContext = createContext<IRemovePositionModalUI>(
  {} as IRemovePositionModalUI,
);

export const RemovePositionUIProvider: FC = ({ children }) => {
  const [{ removePositionActiveModalView, positionId, janeId, shiftId }, { removeParam }] =
    useURLParameters<{
      shiftId?: string;
      removePositionActiveModalView: RemovePositionDetailModalViewType;
    }>();
  const shift_id = shiftId ? castStringToInteger(shiftId) : undefined;
  const { pathname } = useLocation();
  const locationId = pathname.split('/')[3];
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const handleClose = useCallback(() => {
    removePositionModalRoutingService.close();
  }, []);

  const goToNextView = useCallback(
    ({ removePositionFlowType = 'JaneConnection' }) => {
      switch (removePositionActiveModalView) {
        case 'RemovePositionView':
          switch (removePositionFlowType) {
            case 'JaneConnection':
              removePositionModalRoutingService.goToView('PositionTeamMembersView');
              break;
            case 'OrderConnection':
              removePositionModalRoutingService.goToView('OpenShiftsView');
              break;
          }
      }
    },
    [removePositionActiveModalView],
  );

  useEffect(() => {
    if (!Object.keys(RemovePositionDetailComponentMap).includes(removePositionActiveModalView)) {
      removeParam(['removePositionActiveModalView', 'shiftId']);
    }
  }, [removePositionActiveModalView, removeParam, shift_id]);

  return (
    <RemovePositionUIContext.Provider
      value={{
        isOpen: !!removePositionActiveModalView,
        onClose: handleClose,
        removePositionActiveModalView:
          removePositionActiveModalView as RemovePositionDetailModalViewType,
        positionId,
        janeId,
        shift_id: shift_id,
        isConfirmDeleteOpen,
        setIsConfirmDeleteOpen,
        locationId,
        goToNextView,
      }}
    >
      {children}
    </RemovePositionUIContext.Provider>
  );
};

export const useRemovePositionModalUIContext = () =>
  useContext<IRemovePositionModalUI>(RemovePositionUIContext);
